import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {IntlShape, useIntl} from 'react-intl';
import {Alert, Card, Form, OverlayTrigger, Tooltip} from 'react-bootstrap';
import Button from '../../../../../Components/Button';
import Loading from '../../../../../app/common/components/Loading';
import {ConfigurationTrunkSip} from './mocks';
import {
  useGetConfigurationTrunkSip,
  useGetTrunkNumeros,
  useSaveConfigurationTrunkSip,
} from '../../../Hooks/TelephonieManager';

/**
 * Configuration technique du trunk (côté SBC/PBX partenaire), persistée par
 * api-telephonie sur le numéro tête de trunk (`TrunkingMainNumber`) — reproduction de
 * l'écran legacy. Les champs sont directement modifiables (pas de bascule
 * lecture/édition côté legacy) ; « Modifier » enregistre. Les listes déroulantes
 * ci-dessous reproduisent les options réellement observées (captures d'écran des menus
 * ouverts) ; celles dont on n'a que la valeur actuellement sélectionnée n'exposent que
 * cette seule option.
 */

/*
const OPTIONS_REDUNDANCY_MODE = [
  'Redondant',
  'Répartition de charge',
  'Redondant NAT',
  'Répartition de charge NAT',
];
const OPTIONS_MEDIA_HOLD_FORMAT = ['Envoi seulement', 'Inactif', 'Envoi/réception'];
const OPTIONS_TRANSPORT_METHOD = ['UDP', 'StaticTCP', 'StaticUDP'];
const OPTIONS_OUI_NON = ['Oui', 'Non'];
*/
const OPTIONS_ADDRESS_PREFIX = [
  'Ne pas modifier',
  'International',
  'National',
  'Préfixe E164',
  'Sans préfixe',
];
/*
const OPTIONS_ALLOW_CALLS = ['Oui (par défaut)', 'Non'];
*/

/** Réutilisée par « Préfixe d'adresse de destination » et « Préfixe d'adresse appelante ». */
const addressPrefixTooltip = (intl: IntlShape): JSX.Element => (
  <>
    <div>{intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ADDRESS_PREFIX_BULLE_INTRO'})}</div>
    <div className="mt-2">
      <strong>Ne pas modifier :</strong>{' '}
      {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ADDRESS_PREFIX_BULLE_NE_PAS_MODIFIER'})}
    </div>
    <div className="mt-2">
      <strong>International :</strong>{' '}
      {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ADDRESS_PREFIX_BULLE_INTERNATIONAL'})}
    </div>
    <div className="mt-2">
      <strong>National :</strong>{' '}
      {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ADDRESS_PREFIX_BULLE_NATIONAL'})}
    </div>
    <div className="mt-2">
      <strong>Préfixe E164 :</strong>{' '}
      {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ADDRESS_PREFIX_BULLE_E164'})}
    </div>
    <div className="mt-2">
      <strong>Sans préfixe :</strong>{' '}
      {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ADDRESS_PREFIX_BULLE_SANS_PREFIXE'})}
    </div>
  </>
);

interface FieldRowProps {
  label: string;
  tooltip?: JSX.Element;
  children: JSX.Element;
}

const FieldRow = ({label, tooltip, children}: FieldRowProps): JSX.Element => (
  <div
    className="d-flex align-items-center justify-content-between gap-4 mb-3"
    style={{maxWidth: 620}}
  >
    <span className="fs-6 d-flex align-items-center gap-2">
      {label}
      {tooltip && (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
          <i className="bi bi-question-circle-fill text-primary cursor-pointer" />
        </OverlayTrigger>
      )}
    </span>
    <div style={{minWidth: 220}}>{children}</div>
  </div>
);

interface SelectRowProps {
  label: string;
  tooltip?: JSX.Element;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

const SelectRow = ({label, tooltip, value, options, onChange}: SelectRowProps): JSX.Element => (
  <FieldRow label={label} tooltip={tooltip}>
    <Form.Select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </Form.Select>
  </FieldRow>
);

interface SectionTitleProps {
  label: string;
}

const SectionTitle = ({label}: SectionTitleProps): JSX.Element => (
  <div className="d-flex align-items-center gap-2 mt-6 mb-4">
    <i className="bi bi-bookmark-fill text-primary" />
    <span className="fw-bold fs-5">{label}</span>
  </div>
);

interface ConfigurerTrunkSipProps {
  serviceId: number;
}

const ConfigurerTrunkSip = ({serviceId}: ConfigurerTrunkSipProps): JSX.Element | null => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {data: trunkNumeros, isLoading: isLoadingNumeros} = useGetTrunkNumeros(serviceId);
  const numeroPrincipal = trunkNumeros?.numeros.find((numero) => numero.principal);
  const {data, isLoading} = useGetConfigurationTrunkSip(serviceId, numeroPrincipal?.numeroId);
  const saveMutation = useSaveConfigurationTrunkSip(serviceId, numeroPrincipal?.numeroId ?? 0);
  const [config, setConfig] = useState<ConfigurationTrunkSip | null>(null);

  useEffect(() => {
    if (data && !config) {
      setConfig(data);
    }
  }, [data, config]);

  // Efface le message de succès/erreur après quelques secondes, comme les autres
  // écrans de la partie téléphonie (`useAutoDismissMessage`) : ici la mutation
  // react-query porte directement l'état affiché, on la réinitialise donc au lieu
  // de gérer un message local.
  useEffect(() => {
    if (!saveMutation.isSuccess && !saveMutation.isError) {
      return undefined;
    }
    const timeout = setTimeout(() => saveMutation.reset(), 5000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveMutation.isSuccess, saveMutation.isError]);

  if (!isLoadingNumeros && !numeroPrincipal) {
    return (
      <Alert variant="danger" className="mt-4">
        {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.NUMERO_PRINCIPAL_INTROUVABLE'})}
      </Alert>
    );
  }

  if (isLoadingNumeros || isLoading || !config) {
    return <Loading className="col-md-6" />;
  }

  const champ = (champUpdate: Partial<ConfigurationTrunkSip>): void => {
    setConfig({...config, ...champUpdate});
  };

  const confirmer = (): void => {
    saveMutation.mutate(config);
  };

  return (
    <div className="mt-4">
      <Card>
        <Card.Body>
          <div className="mx-auto" style={{maxWidth: 620}}>
            <SectionTitle
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.SECTION_EQUIPEMENT'})}
            />
            <FieldRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.MODEL_TYPE_VERSION'})}
            >
              <Form.Control
                value={config.modelTypeVersion}
                onChange={(e) => champ({modelTypeVersion: e.target.value})}
              />
            </FieldRow>

            {/*
            <SectionTitle
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.SECTION_BUSINESS_TRUNK'})}
            />
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.REDUNDANCY_MODE'})}
              tooltip={
                <>
                  <div>
                    <strong>Redondant :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.REDUNDANCY_MODE_DESC_REDONDANT',
                    })}
                  </div>
                  <div className="mt-2">
                    <strong>Répartition de charge :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.REDUNDANCY_MODE_DESC_REPARTITION',
                    })}
                  </div>
                  <div className="mt-2">
                    <strong>Redondant NAT :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.REDUNDANCY_MODE_DESC_REDONDANT_NAT',
                    })}
                  </div>
                  <div className="mt-2">
                    <strong>Répartition de charge NAT :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.REDUNDANCY_MODE_DESC_REPARTITION_NAT',
                    })}
                  </div>
                </>
              }
              value={config.redundancyMode}
              options={OPTIONS_REDUNDANCY_MODE}
              onChange={(value) => champ({redundancyMode: value})}
            />
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ENABLE_REDIRECT'})}
              tooltip={
                <>
                  <div>
                    <strong>Oui :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ENABLE_REDIRECT_DESC_OUI',
                    })}
                  </div>
                  <div className="mt-2">
                    <strong>Non :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ENABLE_REDIRECT_DESC_NON',
                    })}
                  </div>
                </>
              }
              value={config.enableRedirect}
              options={OPTIONS_OUI_NON}
              onChange={(value) => champ({enableRedirect: value})}
            />
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ENABLE_REFER'})}
              tooltip={
                <>
                  <div>
                    <strong>Oui :</strong>{' '}
                    {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ENABLE_REFER_DESC_OUI'})}
                  </div>
                  <div className="mt-2">
                    <strong>Non :</strong>{' '}
                    {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ENABLE_REFER_DESC_NON'})}
                  </div>
                </>
              }
              value={config.enableRefer}
              options={OPTIONS_OUI_NON}
              onChange={(value) => champ({enableRefer: value})}
            />
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.MEDIA_HOLD_FORMAT'})}
              tooltip={
                <div>
                  {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.MEDIA_HOLD_FORMAT_BULLE'})}
                </div>
              }
              value={config.mediaHoldFormat}
              options={OPTIONS_MEDIA_HOLD_FORMAT}
              onChange={(value) => champ({mediaHoldFormat: value})}
            />
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.AUTHENTICATE_USER'})}
              tooltip={
                <>
                  <div>
                    <strong>Oui :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.AUTHENTICATE_USER_DESC_OUI',
                    })}
                  </div>
                  <div className="mt-2">
                    <strong>Non :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.AUTHENTICATE_USER_DESC_NON',
                    })}
                  </div>
                </>
              }
              value={config.authenticateBusinessTrunkUser}
              options={OPTIONS_OUI_NON}
              onChange={(value) => champ({authenticateBusinessTrunkUser: value})}
            />
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.TRANSPORT_METHOD'})}
              tooltip={
                <div>
                  {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.TRANSPORT_METHOD_BULLE'})}
                </div>
              }
              value={config.transportMethod}
              options={OPTIONS_TRANSPORT_METHOD}
              onChange={(value) => champ({transportMethod: value})}
            />
            */}

            {/*
            <SectionTitle
              label={intl.formatMessage({
                id: 'TELEPHONIE.CONFIG_TRUNK_SIP.SECTION_HEADER_ENCODING',
              })}
            />
            <FieldRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.CALL_HISTORY'})}
              tooltip={
                <>
                  <div>
                    {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.CALL_HISTORY_BULLE_INTRO'})}
                  </div>
                  <div className="mt-2">
                    <strong>Aucune :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.CALL_HISTORY_BULLE_AUCUNE',
                    })}
                  </div>
                  <div className="mt-2">
                    <strong>Informations sur l&apos;historique des appels :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.CALL_HISTORY_BULLE_HISTORIQUE',
                    })}
                  </div>
                  <div className="mt-2">
                    <strong>Informations de déviation d&apos;appel :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.CALL_HISTORY_BULLE_DEVIATION',
                    })}
                  </div>
                </>
              }
            >
              <Form.Control value={config.callHistory} disabled readOnly />
            </FieldRow>
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.OWN_DOMAIN_NAME'})}
              tooltip={
                <>
                  <div>
                    <strong>Oui :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.OWN_DOMAIN_NAME_DESC_OUI',
                    })}
                  </div>
                  <div className="mt-2">
                    <strong>Non :</strong>{' '}
                    {intl.formatMessage({
                      id: 'TELEPHONIE.CONFIG_TRUNK_SIP.OWN_DOMAIN_NAME_DESC_NON',
                    })}
                  </div>
                </>
              }
              value={config.ownDomainNameInHostparts}
              options={OPTIONS_OUI_NON}
              onChange={(value) => champ({ownDomainNameInHostparts: value})}
            />
            */}

            <SectionTitle
              label={intl.formatMessage({
                id: 'TELEPHONIE.CONFIG_TRUNK_SIP.SECTION_NUMBER_TRANSLATION',
              })}
            />
            <FieldRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.STRIP_LEADING_DIGITS'})}
              tooltip={
                <div>
                  {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.STRIP_LEADING_DIGITS_BULLE'})}
                </div>
              }
            >
              <Form.Control
                type="number"
                min={0}
                max={8}
                value={config.stripLeadingDigits}
                onChange={(e) =>
                  // min/max ne bornent que les flèches du champ : la saisie directe doit être bornée ici.
                  champ({stripLeadingDigits: Math.min(8, Math.max(0, Math.trunc(Number(e.target.value)) || 0))})
                }
              />
            </FieldRow>
            <SelectRow
              label={intl.formatMessage({
                id: 'TELEPHONIE.CONFIG_TRUNK_SIP.DESTINATION_ADDRESS_PREFIX',
              })}
              tooltip={addressPrefixTooltip(intl)}
              value={config.destinationAddressPrefix}
              options={OPTIONS_ADDRESS_PREFIX}
              onChange={(value) => champ({destinationAddressPrefix: value})}
            />
            <FieldRow
              label={intl.formatMessage({
                id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ADDITIONAL_DESTINATION_PREFIX',
              })}
              tooltip={
                <div>
                  {intl.formatMessage({
                    id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ADDITIONAL_DESTINATION_PREFIX_BULLE',
                  })}
                </div>
              }
            >
              <Form.Control
                value={config.additionalDestinationAddressPrefix}
                onChange={(e) => champ({additionalDestinationAddressPrefix: e.target.value})}
              />
            </FieldRow>
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.CALLING_ADDRESS_PREFIX'})}
              tooltip={addressPrefixTooltip(intl)}
              value={config.callingAddressPrefix}
              options={OPTIONS_ADDRESS_PREFIX}
              onChange={(value) => champ({callingAddressPrefix: value})}
            />
            {/*
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.USER_PHONE'})}
              tooltip={
                <div>
                  <strong>Oui :</strong>{' '}
                  {intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.USER_PHONE_DESC_OUI'})}
                </div>
              }
              value={config.userPhone}
              options={OPTIONS_OUI_NON}
              onChange={(value) => champ({userPhone: value})}
            />

            <SectionTitle
              label={intl.formatMessage({
                id: 'TELEPHONIE.CONFIG_TRUNK_SIP.SECTION_WHITELIST_BLACKLIST',
              })}
            />
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ALLOW_OUTGOING_CALLS'})}
              value={config.allowOutgoingCalls}
              options={OPTIONS_ALLOW_CALLS}
              onChange={(value) => champ({allowOutgoingCalls: value})}
            />
            <SelectRow
              label={intl.formatMessage({id: 'TELEPHONIE.CONFIG_TRUNK_SIP.ALLOW_INCOMING_VOIP'})}
              value={config.allowIncomingDirectCallsToVoipNumbers}
              options={OPTIONS_ALLOW_CALLS}
              onChange={(value) => champ({allowIncomingDirectCallsToVoipNumbers: value})}
            />

            <SectionTitle
              label={intl.formatMessage({
                id: 'TELEPHONIE.CONFIG_TRUNK_SIP.SECTION_CONCURRENT_CALLS',
              })}
            />
            <div className="fs-6">{config.concurrentCalls}</div>
            */}

            {saveMutation.isSuccess && (
              <Alert variant="success" className="mt-6">
                {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.MODIFICATION_EFFECTUEE'})}
              </Alert>
            )}
            {saveMutation.isError && (
              <Alert variant="danger" className="mt-6">
                {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ERREUR_MODIFICATION'})}
              </Alert>
            )}

            <div className="d-flex justify-content-center gap-3 mt-8">
              <Button
                label={intl.formatMessage({id: 'COMMON.BACK'})}
                onClick={() => navigate('/services/telephonie/liste')}
              />
              <Button
                label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.MODIFIER'})}
                onClick={confirmer}
                disabled={saveMutation.isLoading}
              />
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ConfigurerTrunkSip;
